import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, UserPlus, LogIn, Check } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { MKT_CSS, MktHeader } from "./MktShared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function MarketplaceAuth() {
  const [sp] = useSearchParams();
  const asCreator = sp.get("as") === "creator";
  const mode = sp.get("mode") === "register" ? "register" : "login";
  const returnUrl = sp.get("return") || sp.get("returnTo") || (asCreator ? "/creator" : "");
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [reg, setReg] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });

  const afterAuth = (returnTo) => navigate(returnTo && returnTo.startsWith("/") ? returnTo : "/marketplace", { replace: true });

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", loginForm);
      localStorage.setItem("zylo_token", data.token);
      setUser(data.user);
      toast.success(`Welcome back, ${data.user.name}!`);
      afterAuth(returnUrl);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    if (reg.password !== reg.confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(asCreator ? "/vid/register" : "/mkt/register", { name: reg.name, email: reg.email, phone: reg.phone, password: reg.password });
      localStorage.setItem("zylo_token", data.token);
      setUser(data.user);
      toast.success(asCreator ? "Creator account created — add your first video!" : "Account created — welcome to MILTROO!");
      afterAuth(returnUrl);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const switchTo = (m) => {
    const p = new URLSearchParams(sp);
    p.set("mode", m);
    navigate(`/mkt/auth?${p.toString()}`, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <style>{MKT_CSS}</style>
      <MktHeader />
      <div className="max-w-md mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl p-[1.5px]" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB,#7C3AED)" }}>
          <div className="rounded-3xl bg-card p-6 sm:p-8">
            <div className="grid grid-cols-2 gap-1.5 p-1 rounded-full bg-muted mb-6">
              <button data-testid="auth-tab-login" onClick={() => switchTo("login")}
                className={`py-2 rounded-full text-sm font-bold transition-colors ${mode === "login" ? "gradient-bg text-white shadow" : "text-muted-foreground"}`}>Sign In</button>
              <button data-testid="auth-tab-register" onClick={() => switchTo("register")}
                className={`py-2 rounded-full text-sm font-bold transition-colors ${mode === "register" ? "gradient-bg text-white shadow" : "text-muted-foreground"}`}>Register Free</button>
            </div>

            {mode === "login" ? (
              <form onSubmit={doLogin} className="space-y-4" data-testid="mkt-login-form">
                <h1 className="font-display font-extrabold text-2xl tracking-tight text-center">Welcome back</h1>
                <div className="space-y-1.5"><Label>Email</Label><Input data-testid="login-email" type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Password</Label><Input data-testid="login-password" type="password" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} /></div>
                <Button data-testid="login-submit" disabled={busy} className="w-full h-12 rounded-full font-bold gradient-bg text-white border-0 glow-sm">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4 mr-2" />Sign In</>}
                </Button>
              </form>
            ) : (
              <form onSubmit={doRegister} className="space-y-4" data-testid="mkt-register-form">
                <div className="text-center">
                  <h1 className="font-display font-extrabold text-2xl tracking-tight">{asCreator ? "Register as MILTROO Creator" : "Register as MILTROO User"}</h1>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 font-bold mt-1 flex items-center justify-center gap-1">
                    <Check className="h-3.5 w-3.5" /> {asCreator ? "100% FREE — earn via affiliate commissions" : "100% FREE — no membership fee, pay only for purchases"}
                  </p>
                </div>
                <div className="space-y-1.5"><Label>Full Name</Label><Input data-testid="reg-name" required value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Email</Label><Input data-testid="reg-email" type="email" required value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Mobile Number <span className="text-muted-foreground font-normal">(optional)</span></Label><Input data-testid="reg-phone" value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Password</Label><Input data-testid="reg-password" type="password" required minLength={6} value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Confirm Password</Label><Input data-testid="reg-confirm" type="password" required value={reg.confirm} onChange={(e) => setReg({ ...reg, confirm: e.target.value })} /></div>
                <Button data-testid="reg-submit" disabled={busy} className="w-full h-12 rounded-full font-bold gradient-bg text-white border-0 glow-sm">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-2" />Create Free Account</>}
                </Button>
              </form>
            )}

            <div className="mt-6 pt-5 border-t border-border text-center">
              <p className="text-xs text-muted-foreground">Want to sell your skills instead?</p>
              <Link to="/join-freelancer" data-testid="auth-freelancer-link" className="inline-block mt-2 text-sm font-bold text-gradient">
                Become a Freelancer →
              </Link>
              <div className="mt-4 rounded-2xl p-4 text-white" style={{ background: "linear-gradient(135deg,#04150e,#0a1f14)", border: "1px solid rgba(34,197,94,.35)" }} data-testid="mkt-creator-links">
                <p className="text-[13px] font-extrabold flex items-center justify-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#22c55e" }} />
                  MPlay Creator? <span className="text-emerald-400">Create. Share. Earn.</span>
                </p>
                <div className="grid grid-cols-2 gap-2.5 mt-3">
                  <Link to="/creator/login" data-testid="mkt-creator-login"
                    className="h-10 rounded-full text-[13px] font-extrabold flex items-center justify-center border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 transition-colors">
                    Creator Login
                  </Link>
                  <Link to="/creator/join" data-testid="mkt-creator-join"
                    className="h-10 rounded-full text-[13px] font-extrabold flex items-center justify-center text-[#04150e] transition-transform active:scale-[.97]"
                    style={{ background: "linear-gradient(135deg,#4ade80,#16a34a)" }}>
                    Join as Creator — Free
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
